/**
 * AutoBookPromoteBanner — Sticky banner shown whenever the current org has
 * DRAFT GSTR-2B auto-booked purchase_bills waiting for promotion. Renders on
 * the Purchases page + banking-reco pages so users can promote in one click.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2 } from "lucide-react";

export default function AutoBookPromoteBanner() {
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = () => api.get("/gst-reports/gstr2b/pending-drafts")
    .then(r => setDrafts(r.data.items || []))
    .catch(() => {});
  useEffect(() => { load(); }, []);

  if (dismissed || drafts.length === 0) return null;
  const total = drafts.reduce((a, b) => a + (b.total || 0), 0);

  const promote = async (id) => {
    setBusy(true);
    try {
      await api.post(`/gst-reports/gstr2b/promote-draft/${id}`);
      toast.success("Bill promoted to Regular");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    }
    setBusy(false);
  };

  const promoteAll = async () => {
    if (!confirm(`Promote all ${drafts.length} draft bills (₹${total.toLocaleString("en-IN")}) to Regular?`)) return;
    setBusy(true);
    for (const d of drafts) {
      try { await api.post(`/gst-reports/gstr2b/promote-draft/${d.id}`); } catch { /* ignore */ }
    }
    toast.success(`Promoted ${drafts.length} bills`);
    load();
    setBusy(false);
  };

  return (
    <div className="p-4 rounded border border-amber-500/40 bg-amber-500/10 mb-4"
           data-testid="autobook-promote-banner">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">
              {drafts.length} GSTR-2B auto-booked draft bill{drafts.length > 1 && "s"} awaiting promotion
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Total ₹{total.toLocaleString("en-IN")} · These were created from GSTR-2B rows missing in your books.
              Once you reconcile the vendor payment, promote them to Regular.
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={promoteAll} disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="btn-promote-all">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Promote All ({drafts.length})
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDismissed(true)}
                    data-testid="btn-dismiss-banner">Dismiss</Button>
        </div>
      </div>
      <div className="mt-3 max-h-48 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-amber-500/10">
            <tr>
              <th className="p-1 text-left">Vendor GSTIN</th>
              <th className="p-1 text-left">Bill #</th>
              <th className="p-1 text-right">Amount</th>
              <th className="p-1">Action</th>
            </tr>
          </thead>
          <tbody>
            {drafts.slice(0, 10).map(d => (
              <tr key={d.id} className="border-t border-amber-500/20">
                <td className="p-1 font-mono">{d.vendor_gstin}</td>
                <td className="p-1 font-mono">{d.bill_no}</td>
                <td className="p-1 text-right">₹{Number(d.total || 0).toLocaleString("en-IN")}</td>
                <td className="p-1 text-center">
                  <Button size="sm" variant="outline"
                            onClick={() => promote(d.id)} disabled={busy}
                            data-testid={`btn-promote-${d.id}`}>
                    Promote
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
